/**
 * Replay Keyboard Shortcuts Hook
 * Handles global keyboard shortcuts for replay functionality
 */

import { useEffect } from 'react';
import { useMainContentTabs } from '../store/layoutStore';
import { useReplaySessionStore } from '../store/replaySessionStore';

type ReplaySendTrigger = {
  send: () => void;
};

type SelectedRequestGetter = {
  getSelectedRequest: () => unknown | null;
  sendToReplay: (request: unknown) => void;
};

const replayTriggers = new Map<string, ReplaySendTrigger>();
const requestGridHandlers = new Map<string, SelectedRequestGetter>();

/**
 * Register a replay trigger for a specific session
 * This allows the keyboard shortcut to trigger the replay action
 */
export function registerReplayTrigger({
  sessionId,
  send,
}: {
  sessionId: string;
  send: () => void;
}) {
  replayTriggers.set(sessionId, { send });
  return () => {
    replayTriggers.delete(sessionId);
  };
}

/**
 * Register a request grid handler for a specific tab
 * This allows the keyboard shortcut to send selected requests to Replay
 */
export function registerRequestGridHandler({
  tabId,
  getSelectedRequest,
  sendToReplay,
}: {
  tabId: string;
  getSelectedRequest: () => unknown | null;
  sendToReplay: (request: unknown) => void;
}) {
  requestGridHandlers.set(tabId, { getSelectedRequest, sendToReplay });
  return () => {
    requestGridHandlers.delete(tabId);
  };
}

/**
 * Hook to enable replay keyboard shortcuts
 * Cmd+R (Mac) or Ctrl+R (Windows/Linux) - Send replay request
 */
export function useReplayKeyboard() {
  const { activeTabId, tabs } = useMainContentTabs();
  const { activeSessionId } = useReplaySessionStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if event was already handled by another handler
      if (event.defaultPrevented) return;

      const isModKey = event.metaKey || event.ctrlKey;

      // Only handle if no other modifiers are pressed
      if (event.shiftKey || event.altKey) {
        return;
      }

      // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) - Send Replay Request on Replay tab
      if (isModKey && event.key === 'Enter') {
        if (!tabs || !activeTabId) {
          return;
        }

        const activeTab = tabs.find((tab) => tab.id === activeTabId);

        // Only handle on Replay tab
        if (activeTab?.type === 'replay' && activeSessionId) {
          event.preventDefault();
          const trigger = replayTriggers.get(activeSessionId);
          if (trigger) {
            trigger.send();
          }
        }
        return;
      }

      // Cmd+R (Mac) or Ctrl+R (Windows/Linux) - Send Replay Request / Send to Replay
      if (isModKey && event.key === 'r') {
        // Check if we're on a replay tab
        if (!tabs || !activeTabId) {
          return;
        }

        const activeTab = tabs.find((tab) => tab.id === activeTabId);

        // Prevent default browser reload
        event.preventDefault();

        // Case 1: On a Replay tab - send current request
        if (activeTab?.type === 'replay' && activeSessionId) {
          const trigger = replayTriggers.get(activeSessionId);
          if (trigger) {
            trigger.send();
          }
          return;
        }

        // Case 2: On a requests tab (global requests, endpoint requests, etc)
        // Check if there's a registered grid handler for this tab
        const gridHandler = requestGridHandlers.get(activeTabId);
        if (gridHandler) {
          const selectedRequest = gridHandler.getSelectedRequest();
          if (selectedRequest) {
            gridHandler.sendToReplay(selectedRequest);
          }
          return;
        }
      }
    };

    // Use capture phase to intercept events before they reach the textarea/input elements
    // This ensures our keyboard shortcuts work even when focus is inside an editor
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [activeTabId, tabs, activeSessionId]);
}
